import { useMemo, useState } from "react"
import { toast } from "react-toastify"

import {
  AlertRulesQueryParams,
  useGetContractorAlertRulesQuery,
  useGetDeviceAlertRulesQuery,
  useUpdateContractorAlertRuleMutation,
  useUpdateDeviceAlertRuleMutation,
} from "store/api/alertRulesApi"
import { UITypes } from "types"
import { TableActionButtonGroup } from "types/table/tableActionButton"

import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertRuleEditModal } from "modals/AlertRuleEditModal"
import { AlertRuleDraft, AlertRuleVariant } from "modals/AlertRuleEditModal/types"
import { ToggleButton } from "components/form/ToggleButton"
import TableWrapper from "components/table/TableWrapper"

import { AlertRuleConfiguration, ContractorAlertRuleUpdateBody } from "./alertRules.types"
import { buildParametersFromDraft, getAlertRuleVariant, getInitialDraftFromRule } from "./alertRulesCatalog"
import { AlertRuleRowItem, alertRulesTableHeaderCells, mapAlertRulesToTableRows } from "./alertRulesTableData"
import { AlertRuleToggleConfirmationModal } from "./AlertRuleToggleConfirmationModal"

interface AlertRulesTableProps {
  deviceId?: number
  editConfirmButtonText?: string
}

const getVariantFromRule = (rule: AlertRuleConfiguration) => getAlertRuleVariant(rule.alert_rule_id)

const getAlertRuleQueryParams = (params: Record<string, string>): AlertRulesQueryParams => ({
  name: params.name,
  orderColumn:
    params.orderColumn === "name" ||
    params.orderColumn === "is_enabled" ||
    params.orderColumn === "triggered_devices_count"
      ? params.orderColumn
      : undefined,
  orderType: params.orderType === "ASC" || params.orderType === "DESC" ? params.orderType : undefined,
})

const buildUpdateBody = (
  rule: AlertRuleRowItem,
  draft?: AlertRuleDraft,
  options?: { toggleEnabled?: boolean; deviceIds?: number[]; clearDeviceOverrides?: boolean },
): ContractorAlertRuleUpdateBody => {
  const body: ContractorAlertRuleUpdateBody = {}

  if (typeof options?.toggleEnabled === "boolean") {
    body.is_enabled = options.toggleEnabled
  }

  if (options?.deviceIds?.length) {
    body.device_ids = options.deviceIds
  }

  if (options?.clearDeviceOverrides) {
    body.clear_device_overrides = true
  }

  if (draft) {
    body.parameters = buildParametersFromDraft(rule.alert_rule_id, draft)
  }

  return body
}

export const AlertRulesTable = ({ deviceId, editConfirmButtonText = "Save" }: AlertRulesTableProps) => {
  const isDeviceScope = typeof deviceId === "number"
  const [selectedRule, setSelectedRule] = useState<AlertRuleRowItem | null>(null)
  const [pendingToggleRule, setPendingToggleRule] = useState<AlertRuleRowItem | null>(null)
  const { urlQueryParams } = useUrlQueryParams()

  const alertRuleQueryParams = useMemo(() => getAlertRuleQueryParams(urlQueryParams), [urlQueryParams])

  const contractorQuery = useGetContractorAlertRulesQuery(alertRuleQueryParams, { skip: isDeviceScope })
  const deviceQuery = useGetDeviceAlertRulesQuery(
    { deviceId: deviceId as number, ...alertRuleQueryParams },
    { skip: !isDeviceScope || !deviceId },
  )

  const rules = isDeviceScope ? deviceQuery.data?.data : contractorQuery.data?.data

  const filteredRules = useMemo(() => {
    const status = urlQueryParams.is_enabled

    if (!status) return rules

    return rules?.filter(rule => (rule.is_enabled ? "1" : "0") === status)
  }, [rules, urlQueryParams.is_enabled])

  const [updateContractorRule, contractorUpdateState] = useUpdateContractorAlertRuleMutation()
  const [updateDeviceRule, deviceUpdateState] = useUpdateDeviceAlertRuleMutation()

  const tableData = useMemo(
    () => mapAlertRulesToTableRows(filteredRules, isDeviceScope ? "device" : "contractor"),
    [filteredRules, isDeviceScope],
  )

  const handleRuleUpdate = async (
    rule: AlertRuleRowItem,
    draft?: AlertRuleDraft,
    options?: { toggleEnabled?: boolean; deviceIds?: number[]; clearDeviceOverrides?: boolean },
  ): Promise<boolean> => {
    const body = buildUpdateBody(rule, draft, options)

    try {
      if (isDeviceScope && deviceId) {
        await updateDeviceRule({ deviceId, alertRuleId: rule.alert_rule_id, body }).unwrap()
      } else {
        await updateContractorRule({ alertRuleId: rule.alert_rule_id, body }).unwrap()
      }
      return true
    } catch (error: unknown) {
      toast.error(getErrorMessage(error as Parameters<typeof getErrorMessage>[0]) || "Failed to update alert rule")
      return false
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow): TableActionButtonGroup => {
    const rule = rowData.rowSettings.item as AlertRuleRowItem | undefined

    if (!rule) {
      return { actionButtons: [] }
    }

    const isActive = Boolean(rule.is_enabled)

    return {
      actionButtons: [
        {
          icon: (
            <ToggleButton
              id={`alert-rule-toggle-${rowData.rowSettings.id}`}
              isChecked={isActive}
              onChange={() => setPendingToggleRule(rule)}
            />
          ),
          tooltip: isActive ? "Disable rule" : "Enable rule",
          tooltipPosition: "top",
          handler: () => setPendingToggleRule(rule),
        },
        {
          icon: "Edit",
          tooltip: "Edit rule",
          tooltipPosition: "top",
          handler: () => setSelectedRule(rule),
        },
      ],
    }
  }

  const handleToggleRuleConfirm = () => {
    if (!pendingToggleRule) return
    void handleRuleUpdate(pendingToggleRule, undefined, { toggleEnabled: !pendingToggleRule.is_enabled })
    setPendingToggleRule(null)
  }

  const handleSave = async (_variant: AlertRuleVariant, draft: AlertRuleDraft) => {
    if (!selectedRule) return

    const updated = await handleRuleUpdate(selectedRule, draft)

    if (updated) {
      setSelectedRule(null)
    }
  }

  const handleBulkApply = async (deviceIds: number[], nextIsActive: boolean) => {
    if (!selectedRule) return

    await handleRuleUpdate(selectedRule, undefined, {
      toggleEnabled: nextIsActive,
      deviceIds,
    })
  }

  const handleApplyToAllThermostats = async (draft: AlertRuleDraft) => {
    if (!selectedRule) return false

    return handleRuleUpdate(selectedRule, draft, {
      clearDeviceOverrides: true,
      toggleEnabled: Boolean(selectedRule.is_enabled),
    })
  }

  return (
    <>
      <TableWrapper
        tableName={isDeviceScope ? "contractor-device-alert-rules" : "contractor-alert-rules"}
        headerCells={alertRulesTableHeaderCells}
        data={tableData}
        fitContainer={true}
        getRowActions={getRowActions}
        isLoading={
          contractorUpdateState.isLoading ||
          deviceUpdateState.isLoading ||
          contractorQuery.isFetching ||
          deviceQuery.isFetching
        }
      />
      {pendingToggleRule && (
        <AlertRuleToggleConfirmationModal
          isActive={Boolean(pendingToggleRule.is_enabled)}
          targetDescription={isDeviceScope ? `thermostat ${deviceId}` : undefined}
          thermostatSn={isDeviceScope ? String(deviceId) : undefined}
          onConfirm={handleToggleRuleConfirm}
          onCancel={() => setPendingToggleRule(null)}
        />
      )}
      <AlertRuleEditModal
        variant={selectedRule ? getVariantFromRule(selectedRule) : null}
        initialDraft={
          selectedRule ? getInitialDraftFromRule(selectedRule.alert_rule_id, selectedRule.parameters) : null
        }
        onClose={() => setSelectedRule(null)}
        onSave={handleSave}
        onBulkApply={isDeviceScope ? undefined : handleBulkApply}
        onApplyToAllThermostats={isDeviceScope ? undefined : handleApplyToAllThermostats}
        confirmButtonText={editConfirmButtonText}
      />
    </>
  )
}
